/**
 * PasskeyEnrollPrompt — W2-02 Step 2: one-tap passkey enrollment offered right
 * after a PASSWORD sign-in ("Skip the password next time?").
 *
 * Mirrors UpdatePrompt's pattern: a self-gating fixed bottom-right card,
 * mounted once in AppShell (App.tsx, beside <UpdatePrompt />) so every
 * owner-side landing sees it — including pure managers who land on
 * /manager/approvals (CommandCenter alone would miss them; Login can't host it
 * because both sign-in success paths hard-navigate immediately).
 *
 * Gating (all decided by the pure, unit-tested shouldOfferEnrollPrompt):
 *   - this tab's session began with a PASSWORD sign-in (sessionStorage marker
 *     'nexus_signin_method', written by Login just before the redirect)
 *   - the browser supports passkeys
 *   - NOT a demo session (a passkey enrolled on the shared demo Cognito user
 *     would become a cross-visitor credential — audit §5.3 hard exclusion)
 *   - this device has no local passkey for this user yet
 *   - the user hasn't snoozed the offer ("Not now" = 30 days, per-user)
 *
 * "Set up" -> registerPasskey() (forces platform attachment — the device's own
 * Windows Hello / Face ID / Touch ID, no QR) -> on success the per-user device
 * map is recorded inside registerPasskey and nexus_prefers_password flips to
 * 'false' so the NEXT visit is biometric-primary (the whole point of enrolling).
 */
import { useEffect, useState } from 'react';
import { Fingerprint, CameraOff } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/ui/Toast';
import { useAppStore } from '@/stores/useAppStore';
import { registerPasskey, isPlatformAuthenticatorAvailable, getBiometricLabel, getPasskeyDeviceNote } from '@/lib/auth';
import { isDemoSession } from '@/lib/demo';
import {
  hasLocalPasskey,
  getEnrollSnoozeUntil,
  snoozeEnrollPrompt,
  shouldOfferEnrollPrompt,
  peekSigninMethod,
  clearSigninMethod,
} from '@/lib/passkey-device';

export function PasskeyEnrollPrompt() {
  const user = useAppStore((s) => s.user);
  const toast = useToast();
  const [visible, setVisible] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [biometricLabel] = useState(() => getBiometricLabel());
  const [passkeyDeviceNote] = useState(() => getPasskeyDeviceNote());

  useEffect(() => {
    // Wait for the bootstrap to populate the user — the gates are per-user
    // (local passkey map + snooze are keyed by email).
    if (!user?.email) return;
    const email = user.email;
    let alive = true;
    const signinMethod = peekSigninMethod();
    // Probe for a real platform authenticator before offering — never nudge
    // "set up Windows Hello" on a device that can't actually create one.
    void isPlatformAuthenticatorAvailable().then((platformAvailable) => {
      if (!alive) return;
      const offer = shouldOfferEnrollPrompt({
        signinMethod,
        supported: platformAvailable,
        isDemo: isDemoSession(),
        hasLocal: hasLocalPasskey(email),
        snoozedUntil: getEnrollSnoozeUntil(email),
        now: Date.now(),
      });
      if (offer) {
        setVisible(true);
      } else if (signinMethod) {
        // Marker present but a gate said no (demo / already enrolled / snoozed /
        // no platform authenticator) — consume it so it can't go stale here.
        clearSigninMethod();
      }
    });
    return () => { alive = false; };
  }, [user?.email]);

  if (!visible || !user?.email) return null;

  const dismiss = () => {
    clearSigninMethod();
    setVisible(false);
  };

  const handleEnroll = async () => {
    setEnrolling(true);
    try {
      // registerPasskey records the per-user device map entry itself (auth.ts
      // W2-02) — no extra bookkeeping needed here.
      const result = await registerPasskey();
      if (result.success) {
        toast.success(result.message);
        // Enrolling IS choosing the passkey — flip the durable preference so
        // the next visit leads with the biometric (audit C-4).
        try { localStorage.setItem('nexus_prefers_password', 'false'); } catch { /* */ }
        dismiss();
      } else if (result.cancelled) {
        // Deliberate dismissal of the OS prompt — close WITHOUT snoozing so
        // the next password sign-in re-offers (audit D-2).
        dismiss();
      } else {
        // Real failure — keep the card so the user can retry.
        toast.error(result.message);
      }
    } finally {
      setEnrolling(false);
    }
  };

  const handleNotNow = () => {
    snoozeEnrollPrompt(user.email);
    dismiss();
  };

  return (
    <div
      // Mobile congruence (standing rule): full-width above the bottom nav at
      // 375px, anchored bottom-right card on desktop.
      className="fixed bottom-4 right-4 left-4 sm:left-auto sm:max-w-sm z-50 rounded-xl bg-white dark:bg-slate-800 shadow-2xl ring-1 ring-slate-200 dark:ring-slate-700 p-4"
      role="dialog"
      aria-label="Set up a passkey on this device"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center shrink-0" aria-hidden>
          <Fingerprint className="w-5 h-5 text-primary-600 dark:text-primary-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-900 dark:text-white">Skip the password next time?</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Set up <span className="font-medium">{biometricLabel}</span> on this device — sign in with
            your fingerprint, face, or PIN. No password, no QR code.
          </p>
          {passkeyDeviceNote && (
            <div className="mt-2 flex gap-2 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 px-2.5 py-2">
              <CameraOff className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" aria-hidden />
              <p className="text-[11px] leading-snug text-blue-700 dark:text-blue-300">
                <span className="font-medium">{passkeyDeviceNote.heading}.</span> {passkeyDeviceNote.body}
              </p>
            </div>
          )}
          <div className="flex gap-2 mt-3">
            <Button size="sm" loading={enrolling} onClick={handleEnroll}>
              Set up {biometricLabel}
            </Button>
            <Button size="sm" variant="ghost" onClick={handleNotNow} disabled={enrolling}>
              Not now
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PasskeyEnrollPrompt;
